/*
 * Copyright 2026 Sony Corporation
 */

import { z } from 'zod';

/**
 * Runtime configuration.
 *
 * The deployed bundle is environment-agnostic: `config.json` is written into the
 * web bucket by the stack that owns the ids, so the same artefact can be
 * promoted between deployments without a rebuild. In dev there is no such file,
 * so `VITE_*` variables from `.env.local` stand in.
 */
export const AppConfigSchema = z.object({
  api_url: z.string().url(),
  ws_url: z.string().url(),
  user_pool_id: z.string().min(1),
  user_pool_client_id: z.string().min(1),
  region: z.string().min(1),
  deployment: z.string().min(1),
});
export type AppConfig = z.infer<typeof AppConfigSchema>;

const fromEnv = (): unknown => ({
  api_url: import.meta.env.VITE_API_URL,
  ws_url: import.meta.env.VITE_WS_URL,
  user_pool_id: import.meta.env.VITE_USER_POOL_ID,
  user_pool_client_id: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  region: import.meta.env.VITE_AWS_REGION,
  deployment: import.meta.env.VITE_DEPLOYMENT ?? 'local',
});

/**
 * Reads config.json, or nothing.
 *
 * A dev server answers an unknown path with index.html at status 200, so "the
 * request succeeded" says nothing about whether the body is the configuration.
 * Anything that does not parse is treated as absent, which is exactly the case
 * the environment fallback exists for.
 */
const fromFile = async (): Promise<unknown> => {
  const response = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-store' }).catch(() => undefined);
  if (!response?.ok) {
    return undefined;
  }
  return response.json().catch(() => undefined);
};

export const loadConfig = async (): Promise<AppConfig> => {
  const candidate = (await fromFile()) ?? fromEnv();
  const parsed = AppConfigSchema.safeParse(candidate);

  if (!parsed.success) {
    throw new Error(
      'No usable configuration. Deployed builds read config.json from the web bucket; ' +
        'for local development copy .env.example to .env.local and fill in the stack outputs.'
    );
  }

  return parsed.data;
};
